import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'react-i18next';

import type { BulkExportSummary, BulkExportVersion } from '../types';
import { useValidateBulkItilExport } from '../hooks/useValidateBulkItilExport';
import { itilColors } from '../styles';

type WizardStep = 'version' | 'warning' | 'progress' | 'summary';

type Props = {
  open: boolean;
  ticketIds: number[];
  onClose: () => void;
  onConfirmExport: (
    version: BulkExportVersion,
    missingTicketIds: number[]
  ) => void;
  progress?: { current: number; total: number; ticketId?: number };
  summary?: BulkExportSummary | null;
};

function ItilBulkExportWizard({
  open,
  ticketIds,
  onClose,
  onConfirmExport,
  progress,
  summary
}: Props) {
  const { t } = useTranslation('global');
  const { validateTickets, isValidating } = useValidateBulkItilExport();
  const [step, setStep] = useState<WizardStep>('version');
  const [version, setVersion] = useState<BulkExportVersion>('itil');
  const [missingIds, setMissingIds] = useState<number[]>([]);

  const reset = useCallback(() => {
    setStep('version');
    setVersion('itil');
    setMissingIds([]);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleContinue = async () => {
    if (version === 'original') {
      onConfirmExport('original', []);
      setStep('progress');
      return;
    }

    const results = await validateTickets(ticketIds);
    const missing = results.filter((r) => !r.hasTranslation).map((r) => r.ticketId);
    if (missing.length) {
      setMissingIds(missing);
      setStep('warning');
    } else {
      onConfirmExport('itil', []);
      setStep('progress');
    }
  };

  const handleProceedWithFallback = () => {
    onConfirmExport('itil', missingIds);
    setStep('progress');
  };

  useEffect(() => {
    if (summary) {
      setStep('summary');
    }
  }, [summary]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      {step === 'version' && (
        <>
          <DialogTitle>{t('itilGenius.bulkExport.title')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2, color: itilColors.inkSoft }}>
              {t('itilGenius.bulkExport.count', { count: ticketIds.length })}
            </Typography>
            <RadioGroup
              onChange={(e) => setVersion(e.target.value as BulkExportVersion)}
              value={version}
            >
              <FormControlLabel
                control={<Radio />}
                label={t('itilGenius.bulkExport.versionItil')}
                value="itil"
              />
              <FormControlLabel
                control={<Radio />}
                label={t('itilGenius.bulkExport.versionOriginal')}
                value="original"
              />
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.commonActions.cancel')}</Button>
            <Button disabled={isValidating} onClick={() => void handleContinue()} variant="contained">
              {t('common.commonActions.continue')}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'warning' && (
        <>
          <DialogTitle>{t('itilGenius.bulkExport.warningTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t('itilGenius.bulkExport.warningBody', { count: missingIds.length })}
            </Typography>
            <List dense>
              {missingIds.map((id) => (
                <ListItem key={id} sx={{ py: 0.25 }}>
                  <Typography variant="body2">#{id}</Typography>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setStep('version')}>{t('itilGenius.bulkExport.back')}</Button>
            <Button onClick={handleClose}>{t('itilGenius.bulkExport.cancelExport')}</Button>
            <Button onClick={handleProceedWithFallback} variant="contained">
              {t('itilGenius.bulkExport.continueWithOriginal')}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'progress' && progress && (
        <>
          <DialogTitle>{t('itilGenius.bulkExport.progressTitle')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>
              {t('itilGenius.bulkExport.progressLabel', {
                current: progress.current,
                total: progress.total
              })}
            </Typography>
            {progress.ticketId && (
              <Typography sx={{ mb: 2, color: itilColors.inkSoft }} variant="body2">
                Ticket #{progress.ticketId}
              </Typography>
            )}
            <LinearProgress
              sx={{ height: 8, borderRadius: 1 }}
              value={(progress.current / progress.total) * 100}
              variant="determinate"
            />
          </DialogContent>
        </>
      )}

      {step === 'summary' && summary && (
        <>
          <DialogTitle>{t('itilGenius.bulkExport.summaryTitle')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>
                {t('itilGenius.bulkExport.summaryTotal', { count: summary.total })}
              </Typography>
              <Typography variant="body2">
                {t('itilGenius.bulkExport.summaryItil', { count: summary.itilCount })}
              </Typography>
              <Typography variant="body2">
                {t('itilGenius.bulkExport.summaryOriginal', {
                  count: summary.originalCount
                })}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained">
              {t('common.commonActions.accept')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ItilBulkExportWizard;
